/**
 * recentSearchList — Slack-style "recent N + search-on-demand" list logic.
 *
 * The sidebar can hold many networks/rooms and overwhelm the screen. Default
 * behavior: show the TOP N by recent activity, hide the rest. When the user
 * types a query, filter the FULL list (so an item at index 9+ becomes reachable)
 * — exactly like Slack's channel switcher.
 *
 * Pure + framework-free so it's unit-testable and shared by both lists.
 */

/** Parse an ISO timestamp → epoch ms; 0 (oldest) when missing/invalid so a row
 *  with no activity stamp sorts to the bottom rather than NaN-poisoning sort. */
export function activityTs(value) {
  if (!value) return 0;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : 0;
}

/** Case/space-insensitive substring match of `query` against any of `fields`. */
export function matchesQuery(query, fields) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return (fields || []).some((f) => String(f || '').toLowerCase().includes(q));
}

/**
 * Compute the visible slice for a recent+search list.
 *
 * @param {Array} items        full list (already client-side)
 * @param {object} opts
 * @param {string} opts.query        the (debounced) search string; '' = default view
 * @param {number} [opts.limit=8]    how many to show when NOT searching
 * @param {number} [opts.minQueryLen=1]  min chars before search activates; a
 *        shorter non-empty query yields NO results (avoids noisy 1-2 char matches
 *        and, once search is backend-backed, avoids a fetch per keystroke).
 * @param {(item:any)=>string} opts.getActivity  → the item's activity timestamp
 * @param {(item:any)=>string[]} opts.getSearchFields → strings to match against
 * @param {(item:any)=>boolean} [opts.isPinned]  keep an item visible regardless
 *        (e.g. the currently-selected network/room, so selecting a searched item
 *        that was index 9+ keeps it on screen after the query clears)
 * @returns {{ visible: any[], hiddenCount: number, searching: boolean }}
 */
export function computeRecentSearchList(items, opts = {}) {
  const {
    query = '',
    limit = 8,
    minQueryLen = 1,
    getActivity = () => '',
    getSearchFields = () => [],
    isPinned = () => false,
  } = opts;

  const all = Array.isArray(items) ? items.slice() : [];
  // Most-recent first. Stable-ish: ties keep input order (slice() copy above).
  all.sort((a, b) => activityTs(getActivity(b)) - activityTs(getActivity(a)));

  const q = String(query || '').trim();

  // Below the min length, treat as "searching but nothing yet" — no default
  // list dumped, no matches. Caller shows a "keep typing…" hint.
  if (q && q.length < minQueryLen) {
    return { visible: [], hiddenCount: 0, searching: true, tooShort: true };
  }

  if (q) {
    // Searching → filter the WHOLE list (recency-ordered). Nothing hidden.
    const visible = all.filter((it) => matchesQuery(q, getSearchFields(it)));
    return { visible, hiddenCount: 0, searching: true, tooShort: false };
  }

  // Default view → top `limit` by recency, PLUS any pinned item not already in.
  const top = all.slice(0, Math.max(0, limit));
  const topSet = new Set(top);
  const pinnedExtra = all.filter((it) => isPinned(it) && !topSet.has(it));
  const visible = top.concat(pinnedExtra);
  const hiddenCount = Math.max(0, all.length - visible.length);
  return { visible, hiddenCount, searching: false, tooShort: false };
}
